import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { cn } from '../lib/cn';

/**
 * Unterschriftsfeld für Finger oder Stift. Gebaut für das iPad auf der
 * Baustelle: Pointer-Events statt Touch-Events (deckt Finger, Apple Pencil und
 * Maus mit einem Codepfad ab) und `touch-action: none`, damit das Wischen
 * zeichnet statt die Seite zu scrollen.
 *
 * Der Schriftzug bleibt transparent — im PDF liegt er dadurch auf dem Papier
 * und nicht in einem weißen Kasten.
 */
export interface SignaturePadHandle {
  /** PNG als Data-URL, oder `null`, wenn nichts gezeichnet wurde. */
  toDataUrl: () => string | null;
  clear: () => void;
  isEmpty: () => boolean;
}

export interface SignaturePadProps {
  /**
   * Höhe der Zeichenfläche in CSS-Pixeln — oder `'fill'`, dann füllt sie ihren
   * Container (der braucht dann eine Höhe, z. B. `flex-1`). `'fill'` ist die
   * Grundlage des vergrößerten Unterschriftsfelds.
   */
  height?: number | 'fill';
  className?: string;
  'aria-label'?: string;
  /** Meldet den ersten Strich bzw. das Leeren — für „Übernehmen"-Knöpfe. */
  onChange?: (hasContent: boolean) => void;
}

export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(function SignaturePad(
  { height = 160, className, onChange, ...rest },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const hasContent = useRef(false);
  const [, forceRender] = useState(0);
  const isFill = height === 'fill';

  /** Zeichen-Voreinstellungen nach jedem Bitmap-Reset neu setzen (gehen dabei verloren). */
  function applyPenStyle(ctx: CanvasRenderingContext2D, ratio: number): void {
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
  }

  /**
   * Gesicherter Schriftzug samt der Bitmap-Größe, in der er entstand.
   *
   * Bewusst ein Ref und NICHT jedes Mal frisch aus dem Canvas gelesen: Beim
   * Vergrößern feuert der ResizeObserver zweimal (Container wächst, Layout setzt
   * sich). Der zweite Lauf hätte einen Canvas gelesen, dessen Neuzeichnen noch
   * aussteht — also ein leeres Bild gesichert und den Schriftzug gelöscht
   * (gefunden im Design-Review 2026-07-30: Knopf frei, Fläche leer).
   */
  const content = useRef<{ dataUrl: string; width: number; height: number } | null>(null);

  /** Aktuellen Stand sichern — nach jedem beendeten Strich. */
  const snapshot = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !hasContent.current || canvas.width === 0 || canvas.height === 0) return;
    content.current = {
      dataUrl: canvas.toDataURL('image/png'),
      width: canvas.width,
      height: canvas.height,
    };
  }, []);

  /**
   * Auflösung an das Gerät anpassen. Ohne das ist der Schriftzug auf einem
   * Retina-Display verwaschen — und im PDF sieht man das erst recht.
   *
   * Ein Größenwechsel setzt die Bitmap zurück und würde den Schriftzug löschen.
   * Deshalb wird der gesicherte Stand danach **gleichmäßig eingepasst** neu
   * gezeichnet (nicht verzerrt). Erst das macht das Vergrößern/Verkleinern des
   * Felds und das Drehen des iPads gefahrlos.
   */
  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = typeof window === 'undefined' ? 1 : (window.devicePixelRatio ?? 1);
    const width = canvas.clientWidth || 300;
    const cssHeight = isFill ? canvas.clientHeight || 160 : height;
    const nextWidth = Math.round(width * ratio);
    const nextHeight = Math.round(cssHeight * ratio);
    if (canvas.width === nextWidth && canvas.height === nextHeight) return;

    canvas.width = nextWidth;
    canvas.height = nextHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return; // jsdom / sehr alte Browser
    applyPenStyle(ctx, ratio);

    const saved = content.current;
    if (!saved) return;
    const image = new Image();
    image.onload = () => {
      const current = canvasRef.current;
      // Zwischenzeitlich weitergewachsen? Dann zeichnet der nächste Lauf.
      if (!current || current.width !== nextWidth || current.height !== nextHeight) return;
      const target = current.getContext('2d');
      if (!target) return;
      // Gleichmäßig einpassen und zentrieren — sonst würde der Schriftzug beim
      // Wechsel des Seitenverhältnisses gestreckt.
      const scale = Math.min(nextWidth / saved.width, nextHeight / saved.height);
      const drawWidth = saved.width * scale;
      const drawHeight = saved.height * scale;
      target.save();
      target.setTransform(1, 0, 0, 1, 0, 0); // in Bitmap-Koordinaten zeichnen
      target.drawImage(image, (nextWidth - drawWidth) / 2, (nextHeight - drawHeight) / 2, drawWidth, drawHeight);
      target.restore();
    };
    image.src = saved.dataUrl;
  }, [height, isFill]);

  useEffect(() => {
    resize();
    if (typeof window === 'undefined') return;
    // ResizeObserver, nicht nur `window.resize`: das Feld wächst auch, wenn sich
    // NUR sein Container ändert (Dialog vergrößern) — dabei feuert kein
    // Fenster-Ereignis.
    const canvas = canvasRef.current;
    if (canvas && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => resize());
      observer.observe(canvas);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  function pointOf(event: React.PointerEvent<HTMLCanvasElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function start(event: React.PointerEvent<HTMLCanvasElement>) {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    isDrawing.current = true;
    const { x, y } = pointOf(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function move(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!isDrawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pointOf(event);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasContent.current) {
      hasContent.current = true;
      onChange?.(true);
      forceRender((value) => value + 1);
    }
  }

  function end() {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    // Stand sichern, damit ein Größenwechsel ihn wiederherstellen kann.
    snapshot();
  }

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasContent.current = false;
    content.current = null; // sonst käme der Schriftzug beim nächsten Größenwechsel zurück
    onChange?.(false);
    forceRender((value) => value + 1);
  }, [onChange]);

  useImperativeHandle(
    ref,
    () => ({
      // Ohne Strich bewusst `null` statt eines leeren PNG: ein unsichtbares
      // Bild als Unterschrift zu speichern wäre schlimmer als keine.
      toDataUrl: () => (hasContent.current ? (canvasRef.current?.toDataURL('image/png') ?? null) : null),
      clear,
      isEmpty: () => !hasContent.current,
    }),
    [clear],
  );

  return (
    <div
      className={cn(
        'relative rounded-lg border border-dashed border-border bg-background',
        isFill && 'h-full',
        className,
      )}
    >
      <canvas
        ref={canvasRef}
        role="img"
        style={{ height: isFill ? '100%' : height, touchAction: 'none' }}
        className={cn('w-full cursor-crosshair', isFill && 'block h-full')}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
        {...rest}
      />
      {!hasContent.current && (
        <span className="pointer-events-none absolute inset-x-0 bottom-3 text-center text-xs text-muted-foreground">
          Hier mit dem Finger oder Stift unterschreiben
        </span>
      )}
      <button
        type="button"
        onClick={clear}
        className="absolute right-2 top-2 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        Löschen
      </button>
    </div>
  );
});
